import React from "react";

import NetworkSpeedFormula from "@common/assets/images/releases/ab_aggregation_formula.png";
import Button from "@common/components/Button/Button";
import Heading from "@common/components/Heading/Heading";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import useNetworkSpeedScoring from "@main/pages/Release/consumers/useNetworkSpeedScoring";
import { MetricScoreOutletContext } from "@main/pages/Release/views/MetricDetails/ReleaseMetricDetailsLayout";
import { MetricRow } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";
import * as MetricDetailsStyled from "main/pages/Release/views/MetricDetails/MetricDetails.styles";
import { useOutletContext } from "react-router-dom";

import * as Styled from "./NetworkSpeedScoreView.styles";

export default function NetworkSpeedScoreView() {
  const { refetchReleaseStatus } = useOutletContext<MetricScoreOutletContext>();
  const {
    thresholdRows,
    weightA,
    setWeightA,
    weightB,
    setWeightB,
    weightError,
    scoringConfigLoading,
    handleSave,
    isSaving,
  } = useNetworkSpeedScoring(refetchReleaseStatus);

  if (scoringConfigLoading) {
    return <Spinner />;
  }

  const hasThresholdError = thresholdRows.some((t) => Boolean(t.error));

  return (
    <div style={{ padding: "3.2rem 0" }}>
      <MetricDetailsStyled.MetricsCard>
        <Heading
          heading3
          marginBottom={24}
        >
          Score
        </Heading>
        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Scoring each endpoint
        </Heading>

        <Paragraph>
          {"Lower response times produce higher scores.\n" +
            "Thresholds are measured in milliseconds (ms).\n" +
            "Within each range, the score interpolates linearly between adjacent thresholds. A value exactly halfway between two thresholds gives a score halfway between their assigned scores.\n" +
            "First, each endpoint gets its own score based on these thresholds:"}
        </Paragraph>
        <Styled.ThresholdList>
          {thresholdRows.map((row) => (
            <MetricRow
              key={row.score}
              description={row.description}
              value={row.value}
              error={row.error}
              onChange={row.onChange}
              score={row.score}
              showScore
            />
          ))}
        </Styled.ThresholdList>

        <Heading
          heading4
          fontWeight={"bold"}
          marginBottom={16}
        >
          Aggregation
        </Heading>
        <Paragraph marginBottom={24}>
          {"Then, all those scores are collected and the final 'Endpoints health' score is calculated:"}
        </Paragraph>
        <img
          style={{ marginBottom: "1.6rem" }}
          width={360}
          src={NetworkSpeedFormula}
        />

        <Styled.ThresholdList>
          <MetricRow
            label="a"
            value={weightA}
            onChange={setWeightA}
            description="The percentage weight of the worst-performing endpoint on the Network traffic speed score. The goal is to penalize a single critical failure, as it can jeopardize the entire release. ← System default: 60%."
          />
          <MetricRow
            label="b"
            value={weightB}
            isError={!!weightError}
            onChange={setWeightB}
            description="The percentage weight of the average score on the Network traffic speed score. The sum of a + b must equal 100%. ← System default: 40%."
            error={weightError}
          />
        </Styled.ThresholdList>

        <Button
          type="submit"
          size="small"
          disabled={hasThresholdError || !!weightError || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </MetricDetailsStyled.MetricsCard>
    </div>
  );
}
